import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  CreditGrantEndReason,
  CreditGrantHistoryStatusFilter,
  CreditGrantType,
  DownloadFinanceAttachmentResponse,
  GetCreditApplicationsResponse,
  GetCreditGrantHistoryResponse,
  GetCreditOverviewResponse,
  GetCreditOverdueAccountResponse,
  GetCreditMonitorResponse,
  GetCreditOverdueHeartbeatResponse,
  GetCreditSettingsResponse,
  GetTemporaryCreditApplicationsResponse,
  ListCreditOverdueAccountsResponse,
  OpenCreditEmergencyExemptionResponse,
  RevokeCreditEmergencyExemptionResponse,
  ListFinanceAttachmentsResponse,
  SubmitCreditApplicationResponse,
  SubmitTemporaryCreditApplicationResponse,
  UpdateCreditSettingsRequest,
  UpdateCreditSettingsResponse,
  UploadFinanceAttachmentResponse,
} from '@nadoutong/contracts';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

/**
 * Typed client + React Query hooks for the credit-line surfaces
 * (epic #1033 E4-C1, #1125):
 *   - `/v1/ops/customers/:orgId/credit/*` — per-customer profile, history,
 *     contract upload, and single-sign apply (CM own-book scoped).
 *   - `/v1/ops/credit-settings` — admin-only platform defaults.
 *
 * Money is integer micro-USD end to end; the UI formats but never rounds
 * on the wire. Submit always lands pending_approval (router#1999); the
 * line does not change until a distinct decider approves it.
 */

const SETTINGS_KEY = ['credit-settings'] as const;

export function creditApplicationsQueryKey(orgId: string): readonly [string, string] {
  return ['credit-applications', orgId];
}

// ─── platform defaults (admin) ─────────────────────────────────────────────

export function getCreditSettings(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<GetCreditSettingsResponse> {
  return opsApiRequest<GetCreditSettingsResponse>('/v1/ops/credit-settings', { signal }, fetchImpl);
}

export function useCreditSettings(
  enabled = true,
): UseQueryResult<GetCreditSettingsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: ({ signal }) => getCreditSettings(fetchImpl, signal),
    enabled,
  });
}

export function useUpdateCreditSettings(): UseMutationResult<
  UpdateCreditSettingsResponse,
  Error,
  UpdateCreditSettingsRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      opsApiRequest<UpdateCreditSettingsResponse>(
        '/v1/ops/credit-settings',
        { method: 'PUT', body },
        fetchImpl,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SETTINGS_KEY });
    },
  });
}

// ─── per-customer profile + history ─────────────────────────────────────────

export function getCreditApplications(
  orgId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<GetCreditApplicationsResponse> {
  return opsApiRequest<GetCreditApplicationsResponse>(
    `/v1/ops/customers/${encodeURIComponent(orgId)}/credit/applications`,
    { signal },
    fetchImpl,
  );
}

export function useCreditApplications(
  orgId: string,
  enabled = true,
): UseQueryResult<GetCreditApplicationsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: creditApplicationsQueryKey(orgId),
    queryFn: ({ signal }) => getCreditApplications(orgId, fetchImpl, signal),
    enabled,
  });
}

export function creditOverviewQueryKey(orgId: string): readonly [string, string] {
  return ['credit-overview', orgId];
}

export function getCreditOverview(
  orgId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<GetCreditOverviewResponse> {
  return opsApiRequest<GetCreditOverviewResponse>(
    `/v1/ops/customers/${encodeURIComponent(orgId)}/credit/overview`,
    { signal },
    fetchImpl,
  );
}

export function useCreditOverview(
  orgId: string,
  enabled = true,
): UseQueryResult<GetCreditOverviewResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: creditOverviewQueryKey(orgId),
    queryFn: ({ signal }) => getCreditOverview(orgId, fetchImpl, signal),
    enabled,
  });
}

// ─── contract upload ────────────────────────────────────────────────────────

export interface UploadCreditContractInput {
  readonly orgId: string;
  readonly fileName: string;
  readonly contentType: string;
  readonly dataBase64: string;
}

export function useUploadCreditContract(): UseMutationResult<
  UploadFinanceAttachmentResponse,
  Error,
  UploadCreditContractInput
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      opsApiRequest<UploadFinanceAttachmentResponse>(
        `/v1/ops/customers/${encodeURIComponent(input.orgId)}/credit/contract-attachment`,
        {
          method: 'POST',
          body: {
            file_name: input.fileName,
            content_type: input.contentType,
            data_base64: input.dataBase64,
          },
        },
        fetchImpl,
      ),
    // The read-back list (#1957) is mounted on the same screen as this upload
    // and the client sets no staleTime, so without this the operator who just
    // uploaded the contract keeps being told there is none until a full page
    // reload. On a surface where the contract IS the evidence, failing in that
    // direction is the expensive one.
    onSuccess: (_result, input) => {
      void qc.invalidateQueries({
        queryKey: creditContractAttachmentsQueryKey(input.orgId),
      });
    },
  });
}

// ─── contract read-back (#1957) ─────────────────────────────────────────────

/**
 * The uploaded contract, read back. Two calls because the application only
 * stores the attachment's OBJECT KEY while the download is addressed by id —
 * the list is what resolves one to the other, and it is also what tells a
 * caller "there is nothing here" apart from "you may not have it".
 *
 * These are READS. They must not be gated on `canWrite`: the finance manager
 * who signs stage 2 of the application can write nothing on the customer and
 * still has to see the contract they are signing against.
 */
export function creditContractAttachmentsQueryKey(orgId: string): readonly [string, string] {
  return ['credit-contract-attachments', orgId];
}

export function getCreditContractAttachments(
  orgId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListFinanceAttachmentsResponse> {
  return opsApiRequest<ListFinanceAttachmentsResponse>(
    `/v1/ops/customers/${encodeURIComponent(orgId)}/credit/attachments`,
    { signal },
    fetchImpl,
  );
}

export function useCreditContractAttachments(
  orgId: string,
  enabled = true,
): UseQueryResult<ListFinanceAttachmentsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: creditContractAttachmentsQueryKey(orgId),
    queryFn: ({ signal }) => getCreditContractAttachments(orgId, fetchImpl, signal),
    enabled,
  });
}

export function downloadCreditContractAttachment(
  orgId: string,
  attachmentId: string,
  fetchImpl: typeof fetch,
): Promise<DownloadFinanceAttachmentResponse> {
  return opsApiRequest<DownloadFinanceAttachmentResponse>(
    `/v1/ops/customers/${encodeURIComponent(orgId)}/credit/attachments/${encodeURIComponent(attachmentId)}`,
    {},
    fetchImpl,
  );
}

// ─── single-sign apply ──────────────────────────────────────────────────────

export interface SubmitCreditApplicationInput {
  readonly orgId: string;
  readonly requestedLimitCnyCents: number;
  readonly requestedCycleDays: number;
  readonly contractAttachmentKey: string;
  readonly reason: string;
}

export interface SubmitTemporaryCreditApplicationInput {
  readonly orgId: string;
  readonly requestedLimitCnyCents: number;
  readonly expectedArrivalAmountCnyCents: number;
  readonly expectedArrivalDate: string;
  readonly paymentEvidenceText?: string;
  readonly paymentEvidenceAttachmentKey?: string;
}

export function temporaryCreditApplicationsQueryKey(orgId: string): readonly [string, string] {
  return ['temporary-credit-applications', orgId];
}

export function useSubmitTemporaryCreditApplication(): UseMutationResult<
  SubmitTemporaryCreditApplicationResponse,
  Error,
  SubmitTemporaryCreditApplicationInput
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      opsApiRequest<SubmitTemporaryCreditApplicationResponse>(
        `/v1/ops/customers/${encodeURIComponent(input.orgId)}/credit/temporary-apply`,
        {
          method: 'POST',
          body: {
            requested_limit_cny_cents: input.requestedLimitCnyCents,
            expected_arrival_amount_cny_cents: input.expectedArrivalAmountCnyCents,
            expected_arrival_date: input.expectedArrivalDate,
            ...(input.paymentEvidenceText === undefined
              ? {}
              : { payment_evidence_text: input.paymentEvidenceText }),
            ...(input.paymentEvidenceAttachmentKey === undefined
              ? {}
              : { payment_evidence_attachment_key: input.paymentEvidenceAttachmentKey }),
          },
        },
        fetchImpl,
      ),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: temporaryCreditApplicationsQueryKey(input.orgId) });
    },
  });
}

export interface UploadTemporaryCreditEvidenceInput {
  readonly orgId: string;
  readonly fileName: string;
  readonly contentType: string;
  readonly dataBase64: string;
}

export function temporaryCreditEvidenceQueryKey(orgId: string): readonly [string, string] {
  return ['temporary-credit-evidence', orgId];
}

export function getTemporaryCreditEvidence(
  orgId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListFinanceAttachmentsResponse> {
  return opsApiRequest<ListFinanceAttachmentsResponse>(
    `/v1/ops/customers/${encodeURIComponent(orgId)}/credit/temporary-evidence`,
    { signal },
    fetchImpl,
  );
}

export function useTemporaryCreditEvidence(
  orgId: string,
  enabled = true,
): UseQueryResult<ListFinanceAttachmentsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: temporaryCreditEvidenceQueryKey(orgId),
    queryFn: ({ signal }) => getTemporaryCreditEvidence(orgId, fetchImpl, signal),
    enabled,
  });
}

export function downloadTemporaryCreditEvidence(
  orgId: string,
  attachmentId: string,
  fetchImpl: typeof fetch,
): Promise<DownloadFinanceAttachmentResponse> {
  return opsApiRequest<DownloadFinanceAttachmentResponse>(
    `/v1/ops/customers/${encodeURIComponent(orgId)}/credit/temporary-evidence/${encodeURIComponent(attachmentId)}`,
    {},
    fetchImpl,
  );
}

export function useUploadTemporaryCreditEvidence(): UseMutationResult<
  UploadFinanceAttachmentResponse,
  Error,
  UploadTemporaryCreditEvidenceInput
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      opsApiRequest<UploadFinanceAttachmentResponse>(
        `/v1/ops/customers/${encodeURIComponent(input.orgId)}/credit/temporary-evidence`,
        {
          method: 'POST',
          body: {
            file_name: input.fileName,
            content_type: input.contentType,
            data_base64: input.dataBase64,
          },
        },
        fetchImpl,
      ),
    onSuccess: (_result, input) => {
      void qc.invalidateQueries({
        queryKey: temporaryCreditEvidenceQueryKey(input.orgId),
      });
    },
  });
}

export function useSubmitCreditApplication(): UseMutationResult<
  SubmitCreditApplicationResponse,
  Error,
  SubmitCreditApplicationInput
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      opsApiRequest<SubmitCreditApplicationResponse>(
        `/v1/ops/customers/${encodeURIComponent(input.orgId)}/credit/apply`,
        {
          method: 'POST',
          body: {
            requested_limit_cny_cents: input.requestedLimitCnyCents,
            requested_cycle_days: input.requestedCycleDays,
            contract_attachment_key: input.contractAttachmentKey,
            reason: input.reason,
          },
        },
        fetchImpl,
      ),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: creditApplicationsQueryKey(input.orgId) });
    },
  });
}

export function creditOverdueQueryKey(orgId: string): readonly [string, string] {
  return ['credit-overdue', orgId];
}

export function creditOverdueListQueryKey(): readonly [string] {
  return ['credit-overdue-list'];
}

export function creditOverdueHeartbeatQueryKey(): readonly [string] {
  return ['credit-overdue-heartbeat'];
}

export function creditMonitorQueryKey(): readonly [string] {
  return ['credit-monitor'];
}

export function getCreditOverdue(
  orgId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<GetCreditOverdueAccountResponse> {
  return opsApiRequest<GetCreditOverdueAccountResponse>(
    `/v1/ops/customers/${encodeURIComponent(orgId)}/credit/overdue`,
    { signal },
    fetchImpl,
  );
}

export function useCreditOverdue(
  orgId: string,
  enabled = true,
): UseQueryResult<GetCreditOverdueAccountResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: creditOverdueQueryKey(orgId),
    queryFn: ({ signal }) => getCreditOverdue(orgId, fetchImpl, signal),
    enabled,
  });
}

export function useCreditOverdueList(
  enabled = true,
): UseQueryResult<ListCreditOverdueAccountsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: creditOverdueListQueryKey(),
    queryFn: ({ signal }) =>
      opsApiRequest<ListCreditOverdueAccountsResponse>(
        '/v1/ops/credit-overdue',
        { signal },
        fetchImpl,
      ),
    enabled,
  });
}

export function useCreditOverdueHeartbeat(
  enabled = true,
): UseQueryResult<GetCreditOverdueHeartbeatResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: creditOverdueHeartbeatQueryKey(),
    queryFn: ({ signal }) =>
      opsApiRequest<GetCreditOverdueHeartbeatResponse>(
        '/v1/ops/credit-overdue/heartbeat',
        { signal },
        fetchImpl,
      ),
    enabled,
  });
}

export function useCreditMonitor(
  enabled = true,
): UseQueryResult<GetCreditMonitorResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: creditMonitorQueryKey(),
    queryFn: ({ signal }) =>
      opsApiRequest<GetCreditMonitorResponse>(
        '/v1/ops/credit-monitor',
        { signal },
        fetchImpl,
      ),
    enabled,
  });
}

export interface OpenCreditExemptionInput {
  readonly orgId: string;
  readonly expiresAt: string;
  readonly reason: string;
}

export function useRevokeCreditExemption(): UseMutationResult<
  RevokeCreditEmergencyExemptionResponse,
  Error,
  string
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orgId) =>
      opsApiRequest<RevokeCreditEmergencyExemptionResponse>(
        `/v1/ops/customers/${encodeURIComponent(orgId)}/credit/exemption/revoke`,
        { method: 'POST' },
        fetchImpl,
      ),
    onSuccess: (_data, orgId) => {
      void qc.invalidateQueries({ queryKey: creditOverdueQueryKey(orgId) });
      void qc.invalidateQueries({ queryKey: creditOverdueListQueryKey() });
    },
  });
}

export function useOpenCreditExemption(): UseMutationResult<
  OpenCreditEmergencyExemptionResponse,
  Error,
  OpenCreditExemptionInput
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      opsApiRequest<OpenCreditEmergencyExemptionResponse>(
        `/v1/ops/customers/${encodeURIComponent(input.orgId)}/credit/exemption`,
        {
          method: 'POST',
          body: {
            expires_at: input.expiresAt,
            reason: input.reason,
          },
        },
        fetchImpl,
      ),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: creditOverdueQueryKey(input.orgId) });
      void qc.invalidateQueries({ queryKey: creditOverdueListQueryKey() });
    },
  });
}

// ─── credit grant history (router#2543) ─────────────────────────────────────

export interface CreditGrantHistoryQuery {
  readonly customer?: string;
  readonly manager?: string;
  readonly grantType?: CreditGrantType;
  readonly status?: CreditGrantHistoryStatusFilter;
  readonly endReason?: CreditGrantEndReason;
  readonly startsFrom?: string;
  readonly startsTo?: string;
  readonly limit?: number;
  readonly offset?: number;
}

export function creditGrantHistoryQueryKey(
  params: CreditGrantHistoryQuery,
): readonly ['credit-grant-history', CreditGrantHistoryQuery] {
  return ['credit-grant-history', params];
}

function appendQueryParam(params: URLSearchParams, key: string, value: string | number | undefined): void {
  if (value === undefined) return;
  const text = typeof value === 'number' ? String(value) : value.trim();
  if (text.length === 0) return;
  params.set(key, text);
}

export function buildCreditGrantHistoryPath(params: CreditGrantHistoryQuery): string {
  const query = new URLSearchParams();
  appendQueryParam(query, 'customer', params.customer);
  appendQueryParam(query, 'manager', params.manager);
  appendQueryParam(query, 'grant_type', params.grantType);
  if (params.status !== undefined && params.status !== 'all') {
    query.set('status', params.status);
  }
  appendQueryParam(query, 'end_reason', params.endReason);
  appendQueryParam(query, 'starts_from', params.startsFrom);
  appendQueryParam(query, 'starts_to', params.startsTo);
  if (params.limit !== undefined && Number.isSafeInteger(params.limit)) {
    query.set('limit', String(params.limit));
  }
  if (params.offset !== undefined && Number.isSafeInteger(params.offset) && params.offset > 0) {
    query.set('offset', String(params.offset));
  }
  const encoded = query.toString();
  return encoded.length > 0 ? `/v1/ops/credit/grants?${encoded}` : '/v1/ops/credit/grants';
}

export function getCreditGrantHistory(
  params: CreditGrantHistoryQuery,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<GetCreditGrantHistoryResponse> {
  return opsApiRequest<GetCreditGrantHistoryResponse>(
    buildCreditGrantHistoryPath(params),
    { signal },
    fetchImpl,
  );
}

export function useCreditGrantHistory(
  params: CreditGrantHistoryQuery,
  enabled = true,
): UseQueryResult<GetCreditGrantHistoryResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: creditGrantHistoryQueryKey(params),
    queryFn: ({ signal }) => getCreditGrantHistory(params, fetchImpl, signal),
    enabled,
  });
}

export function getTemporaryCreditApplications(
  orgId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<GetTemporaryCreditApplicationsResponse> {
  return opsApiRequest<GetTemporaryCreditApplicationsResponse>(
    `/v1/ops/customers/${encodeURIComponent(orgId)}/credit/temporary-applications`,
    { signal },
    fetchImpl,
  );
}

export function useTemporaryCreditApplications(
  orgId: string,
  enabled = true,
): UseQueryResult<GetTemporaryCreditApplicationsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: temporaryCreditApplicationsQueryKey(orgId),
    queryFn: ({ signal }) => getTemporaryCreditApplications(orgId, fetchImpl, signal),
    enabled,
  });
}
